import { useTheme } from '@mui/material/styles';

type FeedbackType = 'rating' | 'questions' | 'mixed';

interface Question {
  id: string;
  type: 'radio' | 'checkbox' | 'text';
  question: string;
  options?: string[];
  required?: boolean;
}

interface PageConfig {
  type: FeedbackType;
  title: string;
  subtitle: string;
  ratingLabel?: string;
  placeholder?: string;
  autoShow: boolean;
  questions?: Question[];
}

const FEEDBACK_ENABLED = import.meta.env.VITE_FEEDBACK_ENABLED === 'true';

const PAGE_CONFIGS: Record<string, PageConfig> = {
  '/': {
    type: 'rating',
    title: 'How is your dashboard experience?',
    subtitle: 'Help us improve your learning journey',
    ratingLabel: 'Rate your dashboard experience',
    placeholder: 'What features do you find most helpful? Any suggestions for improvement?',
    autoShow: true
  },
  '/calendar': {
    type: 'questions',
    title: 'Calendar Feedback',
    subtitle: 'Help us improve scheduling',
    autoShow: false,
    questions: [
      {
        id: 'ease_of_use',
        type: 'radio',
        question: 'How easy is it to navigate the calendar?',
        options: ['Very Easy', 'Easy', 'Neutral', 'Difficult', 'Very Difficult'],
        required: true
      },
      {
        id: 'missing_features',
        type: 'checkbox',
        question: 'Which features would you like to see? (Select all that apply)',
        options: ['Color-coded events', 'Export to PDF or Excel','Add to Google Calendar','Session reminders via email or SMS']
      },
      {
        id: 'reminders',
        type: 'radio',
        question: 'How important are reminder notifications to you?',
        options: ['Very Important', 'Important', 'Neutral', 'Not Important'],
        required: true
      },
      {
        id: 'suggestions',
        type: 'text',
        question: 'Any other suggestions for the calendar?'
      }
    ]
  },
  '/course': {
    type: 'mixed',
    title: 'Course Library Feedback',
    subtitle: 'Help us improve course discovery',
    ratingLabel: 'Rate the course browsing experience',
    autoShow: true,
    questions: [
      {
        id: 'course_discovery',
        type: 'radio',
        question: 'How easy is it to find the courses you have enrolled?',
        options: ['Very Easy', 'Easy', 'Neutral', 'Difficult', 'Very Difficult'],
        required: true
      },
      {
        id: 'info_sufficiency',
        type: 'radio',
        question: 'Does the course page provide enough information?',
        options: ['Yes, it has enough info', 'Somewhat, but could use more', 'No, it lacks important details'],
        required: true
      },
      {
        id: 'additional_info',
        type: 'text',
        question: 'What additional information would you like to see on the course page?'
      }      
    ],
  },
  '/engage': {
    type: 'questions',
    title: 'Engage Section Feedback',
    subtitle: 'Shape our content strategy',
    autoShow: false,
    questions: [
      {
        id: 'content_relevance',
        type: 'radio',
        question: 'How relevant is the engage content to your learning goals?',
        options: ['Highly Relevant', 'Somewhat Relevant', 'Neutral', 'Not Very Relevant', 'Not Relevant'],
        required: true
      },
      {
        id: 'preferred_topics',
        type: 'checkbox',
        question: 'Which topics interest you most?',
        options: ['Data Analytics', 'Business Strategy', 'Digital Marketing', 'AI/ML', 'Finance', 'Leadership']
      },
      {
        id: 'learning_format',
        type: 'checkbox',
        question: "What's your preferred learning format?",
        options: ['Video tutorials', 'Interactive courses', 'Reading materials', 'Hands-on projects'],
        required: true
      },
        {
        id: 'suggestions',
        type: 'text',
        question: 'Any other topics interest you most?'
      }
    ]
  }
};

const COURSE_DETAIL_CONFIG: PageConfig = {
  type: 'mixed',
  title: 'Course Content Feedback',
  subtitle: 'Help us enhance your learning experience',
  ratingLabel: 'Rate this course content',
  autoShow: true,
  questions: [
    {
      id: 'content_quality',
      type: 'radio',
      question: 'How would you rate the overall content quality?',
      options: ['Excellent', 'Good', 'Average', 'Poor'],
      required: true
    },
    {
      id: 'content_types',
      type: 'checkbox',
      question: 'Which content types are most helpful to you?',
      options: ['Video lessons (MP4)', 'Reading materials (PDF)', 'Interactive content (HTML)', 'Practice questions (MCQ)', 'Q&A discussions','Virtual Labs']
    },
    {
      id: 'improvements',
      type: 'checkbox',
      question: 'What aspects could be improved?',
      options: ['Video quality', 'Audio clarity', 'More examples', 'Better explanations', 'More practice questions', 'Better Q&A section']
    }
  ],
  placeholder: 'Share specific feedback about videos, PDFs, practice questions, or any other course materials...'
};

const DEFAULT_CONFIG: PageConfig = {
  type: 'rating',
  title: "We'd love your feedback!",
  subtitle: 'Help us make your experience even better',
  ratingLabel: 'Rate your experience',
  placeholder: 'What did you like? What could we improve?',
  autoShow: false
};

const getRatingText = (rating: number | null, theme: ReturnType<typeof useTheme>) => {
  const texts = {
    1: { text: 'Poor', color: '#ef4444', emoji: '😞' },
    2: { text: 'Fair', color: '#f97316', emoji: '😐' },
    3: { text: 'Good', color: '#eab308', emoji: '🙂' },
    4: { text: 'Very Good', color: '#22c55e', emoji: '😊' },
    5: { text: 'Excellent', color: '#10b981', emoji: '🤩' }
  };
  return rating ? texts[rating as keyof typeof texts] : null;
};

export { FEEDBACK_ENABLED, PAGE_CONFIGS, COURSE_DETAIL_CONFIG, DEFAULT_CONFIG, getRatingText };
export type { FeedbackType, Question, PageConfig };